import React, { useState, useMemo, useEffect, useCallback } from "react";
import { TooltipHost, DirectionalHint } from "@fluentui/react";
import { TbSchema } from "react-icons/tb";
import { toast } from "react-toastify";
import { parseJsonToProperties } from "./../../util_glob";
// import { VscCollapseAll } from "react-icons/vsc"; // Not used
// import { sortObjectByKeyOrder, parseJsonToProperties, findBestDisplayKey } from "./../../util_glob"; // Assuming these are correctly implemented
// For the sake of this example, I'll mock these utility functions
const sortObjectByKeyOrder = (obj: any, keyOrder: string[]) => {
    if (!obj || typeof obj !== "object") return {};
    const sorted: Record<string, any> = {};
    const keys = Object.keys(obj);
    keyOrder.forEach((k: any) => {
        if (keys.includes(k)) {
            sorted[k] = obj[k];
            keys.splice(keys.indexOf(k), 1);
        }
    });
    keys.forEach(k => (sorted[k] = obj[k]));
    return sorted;
};

import "./JsonGridGen.css"; // Make sure this CSS file exists and is correctly styled
// import { HiArrowsExpand } from "react-icons/hi"; // Not used

import { MdExpandLess } from "react-icons/md";
import { MdExpandMore } from "react-icons/md";

interface JsonGridGenProps {
    jsonString?: string;
    onDataHighlight: (data: string) => void;
}

// const hidden_fields = ["structured_data"]; // Not used directly, filtered in renderGrid

const JsonGridGen: React.FC<JsonGridGenProps> = ({ jsonString, onDataHighlight }) => {
    const [gridData, setGridData] = useState(() => {
        if (!jsonString) return {};
        try {
            return typeof jsonString === "object" ? jsonString : JSON.parse(jsonString);
        } catch (error) {
            console.error("Failed to parse jsonString:", error);
            toast.error("Invalid JSON input.");
            return {};
        }
    });
    const [collapsedFields, setCollapsedFields] = useState<{ [key: string]: boolean }>({});

    const itmOrd = [
        "invoice_date",
        "contractor_name",
        "contractor_address",
        "contractor_vat_identification",
        "iban",
        "customer_name",
        "customer_address",
        "customer_vat_identification",
        "invoice_total",
        "vat",
        "invoice_currency",
        "item_number",
        "description",
        "item_code",
        "item_measure",
        "quantity",
        "unit_price",
        "total_price",
        "invoice_items"
    ];

    const getSchemeIntoClipboard = (json: any) => {
        const properties = parseJsonToProperties(json);
        const propertiesJson = JSON.stringify(properties, null, 2);
        navigator.clipboard
            .writeText(propertiesJson)
            .then(() => {
                console.log("Copied to clipboard!");
                toast.success("Content Scheme copied to clipboard!");
            })
            .catch(error => {
                console.error("Error copying to clipboard: ", error);
                toast.error("Failed to copy scheme.");
            });
    };

    const updateElementHeight = useCallback((elementId: string, isCollapsing: boolean) => {
        const el = document.getElementById(elementId);
        if (el) {
            if (isCollapsing) {
                el.style.maxHeight = "0px";
                el.style.opacity = "0";
            } else {
                // Temporarily set to auto to get the full scrollHeight
                el.style.maxHeight = "none";
                const scrollHeight = el.scrollHeight;
                el.style.maxHeight = scrollHeight + "px";
                el.style.opacity = "1";

                // Crucial part: After setting maxHeight, ensure it's not immediately reset by a premature re-render
                // We might need to force a reflow or ensure the timing is correct if issues persist.
                // For now, setting it directly should work with the timeout in toggleCollapse.
            }
        }
    }, []);

    const adjustParentHeights = useCallback(
        (childKey: string) => {
            let parentKey = childKey;
            // Traverse up the hierarchy by splitting the key
            while (parentKey.includes("__")) {
                parentKey = parentKey.substring(0, parentKey.lastIndexOf("__"));
                const parentElement = document.getElementById(`collapsible_${parentKey}`);
                if (parentElement && !collapsedFields[parentKey]) {
                    // Only adjust if the parent is expanded
                    // Use a small timeout to allow child's height to be processed by the browser
                    setTimeout(() => {
                        parentElement.style.maxHeight = "none"; // Temporarily remove maxHeight to calculate actual scrollHeight
                        const scrollHeight = parentElement.scrollHeight;
                        parentElement.style.maxHeight = scrollHeight + "px";
                    }, 0); // A timeout of 0ms can defer execution until the stack is clear
                }
            }
        },
        [collapsedFields]
    );

    const toggleCollapse = useCallback(
        (collapseKey: string) => {
            setCollapsedFields(prevState => {
                const isNowCollapsed = !prevState[collapseKey];
                const newState = { ...prevState, [collapseKey]: isNowCollapsed };

                // Update the specific element's height
                // Use a timeout to ensure the DOM has been updated by React's state change before manipulating style
                setTimeout(() => {
                    const el = document.getElementById(`collapsible_${collapseKey}`);
                    if (el) {
                        if (isNowCollapsed) {
                            el.style.maxHeight = "0px";
                            el.style.opacity = "0";
                        } else {
                            // For expansion:
                            // 1. Set maxHeight to 'none' or a very large value to allow content to define height
                            // 2. Get scrollHeight
                            // 3. Set maxHeight to scrollHeight
                            el.style.maxHeight = "none"; // Allow it to expand fully first
                            const scrollHeight = el.scrollHeight;
                            el.style.maxHeight = scrollHeight + "px";
                            el.style.opacity = "1";
                        }
                        // After toggling a child, adjust its parents
                        if (!isNowCollapsed) {
                            // If we just expanded this element
                            adjustParentHeights(collapseKey);
                        } else {
                            // If we just collapsed this element, parents also need to readjust
                            adjustParentHeights(collapseKey); // Parent height might decrease
                        }
                    }
                }, 0); // Timeout helps ensure DOM is ready for measurement

                return newState;
            });
        },
        [adjustParentHeights]
    ); // Added adjustParentHeights to dependency array

    const updateAllHeightsRecursively = useCallback((key: string, isCollapsing: boolean) => {
        const element = document.getElementById(`collapsible_${key}`);
        if (element) {
            if (isCollapsing) {
                element.style.maxHeight = "0px";
                element.style.opacity = "0";
            } else {
                element.style.maxHeight = "none"; // Temporarily set to auto for scrollHeight calculation
                const scrollHeight = element.scrollHeight;
                element.style.maxHeight = scrollHeight + "px";
                element.style.opacity = "1";
            }

            // If this element contains other collapsible children, their state dictates this one's height too.
            // This logic is primarily handled by adjustParentHeights now.
        }
    }, []);

    const toggleLevel = useCallback(
        (targetLevel: number) => {
            setCollapsedFields(prev => {
                const next = { ...prev };
                let shouldCollapseCurrentLevel = false;
                const levelElements = document.querySelectorAll(`.grid_row.level_${targetLevel}_gen [id^="collapsible_"]`);

                if (levelElements.length > 0) {
                    // Check if any element at the target level is currently expanded
                    shouldCollapseCurrentLevel = Array.from(levelElements).some(el => {
                        const id = (el as HTMLElement).id.slice("collapsible_".length);
                        return !prev[id]; // If not in prev (undefined) or explicitly false, it's expanded
                    });
                } else {
                    // If no elements at this level, find the effective action based on deeper or shallower levels
                    // This part might need more sophisticated logic if strict "toggle" means "invert all at level"
                    // For now, let's assume if no elements, it tries to expand up to this level.
                    shouldCollapseCurrentLevel = false; // Default to expanding if level is empty or not determinable
                }

                // Determine the overall action: if we are collapsing the targetLevel, then deeper levels also collapse.
                // If we are expanding the targetLevel, then shallower levels up to target should expand.
                const maxLevel = 3; // Assuming max 3 levels for now, adjust if different

                for (let l = 0; l <= maxLevel; l++) {
                    // Iterate all possible levels
                    document.querySelectorAll(`.grid_row.level_${l}_gen [id^="collapsible_"]`).forEach(el => {
                        const box = el as HTMLElement;
                        const id = box.id.slice("collapsible_".length);
                        let newCollapsedState;

                        if (shouldCollapseCurrentLevel) {
                            // We are collapsing
                            if (l >= targetLevel) {
                                // Collapse target level and deeper
                                newCollapsedState = true;
                            } else {
                                // Keep shallower levels as they were or ensure expanded
                                newCollapsedState = next[id] === undefined ? false : next[id]; // Default to expanded or keep state
                            }
                        } else {
                            // We are expanding
                            if (l <= targetLevel) {
                                // Expand target level and shallower
                                newCollapsedState = false;
                            } else {
                                // Keep deeper levels as they were
                                newCollapsedState = next[id] === undefined ? true : next[id]; // Default to collapsed or keep state
                            }
                        }
                        next[id] = newCollapsedState;
                    });
                }
                setCollapsedFields(next); // Set state first

                // Apply styles after state update
                setTimeout(() => {
                    for (const key in next) {
                        updateAllHeightsRecursively(key, next[key]);
                    }
                    // After toggling a level, all affected parents need recalculation
                    // This is a bit broad, but ensures consistency.
                    // A more targeted approach would be to find the common ancestors of all toggled elements.
                    Object.keys(next).forEach(key => {
                        if (!next[key]) adjustParentHeights(key); // If expanded, adjust its parents
                    });
                }, 0);

                return next; // This return is for the setCollapsedFields updater function
            });
        },
        [updateAllHeightsRecursively, adjustParentHeights]
    );

    const renderGrid = (data: any, level = 0, parentPath: string[] = []): JSX.Element => {
        let currentData = data;
        // Safely access nested 'data' property if it exists
        if (typeof data === "object" && data !== null && "data" in data && level === 0) {
            currentData = data.data;
        }

        return (
            <div style={{ width: "100%" }} role="group">
                {" "}
                {/* Added role for better a11y for groupings */}
                {Object.entries(sortObjectByKeyOrder(currentData, itmOrd))
                    ?.filter(([key]) => key !== "structured_data")
                    ?.map(([key, value]) => {
                        const isCollapsible = typeof value === "object" && value !== null && Object.keys(value).length > 0;
                        const fullPath = [...parentPath, key];
                        const collapseKey = fullPath.join("__");
                        // Initialize collapsed state if not already set (e.g. all start expanded or collapsed)
                        // For this example, let's default to collapsed unless it's level 0 or 1
                        const isCollapsed = collapsedFields[collapseKey] === undefined ? level > 0 : collapsedFields[collapseKey];

                        return isCollapsible ? (
                            <div key={collapseKey} className={`grid_row level_${level}_gen`} style={{ marginBottom: 2, marginLeft: "2px" }}>
                                <div
                                    style={{
                                        fontWeight: 600,
                                        fontSize: "10px",
                                        color: "#222",
                                        marginBottom: 2,
                                        width: "95%",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        paddingRight: "2px"
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        toggleCollapse(collapseKey);
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            toggleCollapse(collapseKey);
                                        }
                                    }}
                                    tabIndex={0}
                                    aria-expanded={!isCollapsed}
                                    role="button"
                                    aria-controls={`collapsible_${collapseKey}`}
                                >
                                    <span style={{ display: "inline-block", marginRight: 6, fontSize: 14, transition: "transform 0.2s" }}>
                                        {isCollapsed ? <MdExpandMore /> : <MdExpandLess />}
                                    </span>
                                    {key.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}
                                </div>
                                <div
                                    id={`collapsible_${collapseKey}`}
                                    className={`collapsible-section${isCollapsed ? " collapsed" : " expanded"}`}
                                    style={{
                                        overflow: "hidden",
                                        maxHeight: isCollapsed ? "0px" : "20000px", // Initial large maxHeight for expanded
                                        opacity: isCollapsed ? 0 : 1,
                                        marginLeft: 5,
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                        transition: "max-height 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.35s cubic-bezier(0.4,0,0.2,1)"
                                    }}
                                >
                                    {/* Render children only if not collapsed, or always render and rely on CSS.
                                        Always rendering helps with height calculation but can be less performant for very large trees.
                                        For now, always render and let CSS handle visibility.
                                    */}
                                    {renderGrid(value, level + 1, fullPath)}
                                </div>
                            </div>
                        ) : (
                            <div
                                key={collapseKey}
                                className={`grid_row level_${level}_gen`}
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    borderBottom: "1px solid #eee",
                                    fontSize: "8px",
                                    padding: "2px 0 2px 0",
                                    marginLeft: `2px` // Indent simple rows too
                                }}
                            >
                                <div style={{ fontWeight: 500, flexShrink: 0, fontSize: "8px", color: "#222", paddingRight: 8, userSelect: "none" }}>
                                    {key.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}:
                                </div>
                                <div
                                    className={`value_link`}
                                    style={{
                                        color: "#0078d4",
                                        fontWeight: 400,
                                        paddingLeft: "4px",
                                        fontSize: "8px",
                                        cursor: "pointer",
                                        whiteSpace: "pre-line",
                                        wordBreak: "break-all"
                                    }}
                                    onClick={() => onDataHighlight(String(value))}
                                    title={String(value)}
                                >
                                    {String(value)}
                                </div>
                            </div>
                        );
                    })}
            </div>
        );
    };

    // useEffect to set initial heights correctly after the first render for initially expanded items
    useEffect(() => {
        Object.entries(collapsedFields).forEach(([key, isCollapsed]) => {
            if (!isCollapsed) {
                // If it should be expanded
                const el = document.getElementById(`collapsible_${key}`);
                if (el) {
                    el.style.maxHeight = "none"; // Allow full expansion
                    const scrollHeight = el.scrollHeight;
                    el.style.maxHeight = scrollHeight + "px";
                    el.style.opacity = "1";
                    adjustParentHeights(key); // Also adjust parents of initially expanded items
                }
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount after initial collapsedFields are set or gridData loads. Add gridData if it can change and require re-init.

    const gridRendition = useMemo(() => {
        // Initialize collapsed state for all collapsible items if not already set
        // This ensures all items have a defined state for the initial render
        const initialCollapsed: { [key: string]: boolean } = {};
        function populateInitialCollapsed(data: any, currentPath: string[] = [], level = 0) {
            if (typeof data !== "object" || data === null) return;
            Object.keys(data).forEach(key => {
                const newPath = [...currentPath, key];
                const collapseKey = newPath.join("__");
                if (typeof data[key] === "object" && data[key] !== null && Object.keys(data[key]).length > 0) {
                    // Default to collapsed for levels deeper than 0, expanded for level 0.
                    initialCollapsed[collapseKey] = level > 0; // Level 0 expanded, others collapsed
                    populateInitialCollapsed(data[key], newPath, level + 1);
                }
            });
        }

        let dataToParse = gridData;
        if (typeof gridData === "object" && gridData !== null && "data" in gridData) {
            dataToParse = gridData.data;
        }

        if (Object.keys(collapsedFields).length === 0 && Object.keys(dataToParse || {}).length > 0) {
            populateInitialCollapsed(dataToParse);
            setCollapsedFields(initialCollapsed); // This will trigger a re-render
        }

        if (!gridData || Object.keys(gridData).length === 0) return <div className="json-grid-empty">No data to display.</div>;
        return <div className="json-grid">{renderGrid(gridData)}</div>;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridData, collapsedFields]); // renderGrid is not stable, but its dependencies are gridData and collapsedFields indirectly.

    return (
        <div className="json_grid_main">
            <div className="json_grid_header">
                <div>Data</div>
                <div style={{ display: "flex", gap: "3px", margin: "2px 0" }}>
                    <button onClick={() => toggleLevel(0)} aria-label="Toggle Level 0 Sections" className="level-toggle-button">
                        L0
                    </button>
                    <button onClick={() => toggleLevel(1)} aria-label="Toggle Level 1 Sections" className="level-toggle-button">
                        L1
                    </button>
                    <button onClick={() => toggleLevel(2)} aria-label="Toggle Level 2 Sections" className="level-toggle-button">
                        L2
                    </button>
                </div>
                <div
                    onClick={() => {
                        getSchemeIntoClipboard(gridData["data"] || gridData);
                    }}
                    className="exportScheme"
                    role="button" // Added role
                    tabIndex={0} // Added tabIndex
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") getSchemeIntoClipboard(gridData["data"] || gridData);
                    }} // Added keyboard accessibility
                    aria-label="Copy content schema" // Added aria-label
                >
                    <TooltipHost
                        closeDelay={500}
                        content="Copy Content Schema. Can be used in Extraction Settings, to define a Document Type"
                        directionalHint={DirectionalHint.topCenter}
                    >
                        <TbSchema size={13} />
                    </TooltipHost>
                </div>
            </div>
            <div
                className="json_grid_content"
                style={{ overflowY: "auto", overflowX: "hidden", position: "relative", width: "100%" /* Changed from calc(109%) */ }}
            >
                {gridRendition}
            </div>
        </div>
    );
};

export default JsonGridGen;
